import { Avatar, Box, Grid, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLanguageContext } from "../../../contexts/LanguageContext";
import DefaultConstants from "../../../data/Constants";
import Dialog, { IDefaultDialogProps } from "../../Dialog";
import Empty from "../../Empty";
import IServiceOrder from "../../../interfaces/IServiceOrder";
import { Send } from "@mui/icons-material";
import { useAuthContext } from "../../../contexts/AuthContext";
import DateTimeHelper from "../../../helpers/DateTimeHelper";
import useState from "../../../hooks/useState";
import { ServiceStatusEnum } from "../../../data/Enums";
import FormHelper from "../../../helpers/FormHelper";
import ServiceOrderCommentService from "../../../services/ServiceOrderCommentService";


interface ICommentsDialogProps extends IDefaultDialogProps {
    handleRefreshSelectedServiceOrder: () => void;
    serviceOrder: IServiceOrder;
}

interface IStateValues {
    message: string;
}


const CommentsDialog: React.FunctionComponent<ICommentsDialogProps> = ({
    open,
    handleClose,
    handleRefreshSelectedServiceOrder,
    serviceOrder
}) => {
    const { translate } = useLanguageContext();
    const { user } = useAuthContext();

    const state = useState<IStateValues>({
        message: ''
    });

    const contentRef = useRef<HTMLDivElement | null>(null);


    const scrollContentToBottom = useCallback(() => {
        if (contentRef.current === null) {
            return;
        }

        contentRef.current.scrollTo({
            top: contentRef.current.scrollHeight,
            behavior: 'smooth'
        });
    }, [contentRef]);


    useEffect(() => {
        scrollContentToBottom();
        // eslint-disable-next-line
    }, [open, serviceOrder.comments]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (state.values.message.trim().length === 0) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'serviceOrderId', serviceOrder.id);
        
        ServiceOrderCommentService.AddEdit(formData).then(() => {
            handleRefreshSelectedServiceOrder();
            state.reset();
        });
        // eslint-disable-next-line
    }, [serviceOrder, state.values.message, translate]);


    const dialogActions = useMemo(() => {
        const allowAddComment = serviceOrder.statusId !== ServiceStatusEnum.Canceled && serviceOrder.statusId !== ServiceStatusEnum.Completed;
        
        if (allowAddComment) {
            return <Box className='serviceordercomments-dialog-footer'>
                <input 
                    className="message-textfield" 
                    name="message" 
                    value={state.values.message} 
                    onChange={e => state.update(e.target.name, e.target.value)}
                    placeholder={translate('typeMessage')} 
                />

                <Box className='send-box'>
                    <IconButton size="large" className='send-iconbutton' type="submit">
                        <Send />
                    </IconButton>
                </Box>
            </Box>;
        }

        return <Box className='serviceordercomments-dialog-footer'>
            <Typography className='fontsize-14 text-color-2'>
                {translate('_serviceOrderDialogNotAllowAddCommentsDescription')}
            </Typography>
        </Box>;
        // eslint-disable-next-line
    }, [translate, serviceOrder, state.values.message]);


    const getDateLabel = useCallback((date: Date) => {
        const today = DateTimeHelper.Now('YYYY-MM-DD');
        if (DateTimeHelper.Equals(date, today)) {
            return translate('today');
        }

        const yesterday = DateTimeHelper.Remove(today, 1, 'days', 'YYYY-MM-DD');
        if (DateTimeHelper.Equals(date, yesterday)) {
            return translate('yesterday');
        }

        return DateTimeHelper.Format(date) ?? '';
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('comments')}
            dialogActions={dialogActions}
            className='dialogactions-paddingnone overflow-inherit serviceordercomments-dialog'
            absolute={true}
            dividers={true}
            onSubmit={handleSubmit}
            contentRef={contentRef}
        >
            <Grid 
                container 
                columnSpacing={DefaultConstants.gridColumnSpacing} 
                rowSpacing={DefaultConstants.gridRowSpacing}
                paddingY={2}
            >
                {
                    serviceOrder.comments === undefined || serviceOrder.comments.length === 0 ? <>
                        <Grid item xs={12}>
                            <Empty text="noCommentsFound" />
                        </Grid>
                    </> : <>
                        {
                            serviceOrder.comments.map((c, index) => {
                                const className = c.createdBy.token === user?.token ? 'my-message' : '';
                                const date = getDateLabel(c.createdAt);
                                const time = DateTimeHelper.Format(c.createdAt, 'HH:mm') ?? '';
                                
                                return <Grid item xs={12} key={index} className={`comment-container ${className}`}>
                                    <Avatar alt={c.createdBy.fullName.toUpperCase()} src={c.createdBy.avatar} className='user-avatar' />

                                    <Box className='message-data'>
                                        <Typography className='message-username'>{c.createdBy.fullName}</Typography>
                                        <Typography className='message-text'>{c.message}</Typography>
                                        
                                        <Typography className='message-datetime'>
                                            {translate('dateAtTime').replace('{date}', date).replace('{time}', time)}
                                        </Typography>
                                    </Box>
                                </Grid>;
                            })
                        }
                    </>
                }
            </Grid>
        </Dialog>
    );
}

export default CommentsDialog;